import { SpecialZoomLevel } from '@react-pdf-viewer/core';
import { zoomOptions } from '../zoomOptions';
import { PageDimensionsMap } from '../../types/pdf';

interface Destination {
  pageNumber: number;
  bottomOffset: number;
  leftOffset: number;
  scaleTo?: number;
}

export interface ViewerSlice {
  destination: Destination | null;
  activeAreaId: string | null;
  setDestination: (destination: Destination | null, areaId?: string) => void;
  resetActiveArea: () => void;
  pageNum: { value: number; exact: boolean };
  setPageNum: (newPageNum: { value: number; exact: boolean }) => void;
  incPageNum: () => void;
  decPageNum: () => void;
  pageCount: number;
  setPageCount: (newPageCount: number) => void;
  pageDimensions: PageDimensionsMap;
  setPageDimensions: (pageDimensions: PageDimensionsMap) => void;
  zoomIndex: number;
  setZoomIndex: (index: number) => void;
  incZoomIndex: () => void;
  decZoomIndex: () => void;
}

type Set = (partial: Partial<ViewerSlice> | ((state: ViewerSlice) => Partial<ViewerSlice>)) => void;

const consecuteIndex = (zoomIndex: number, consecutation: number) => {
  const currentZoom = zoomOptions[zoomIndex];
  if (typeof currentZoom !== 'number') {
    return zoomOptions.indexOf(1);
  }

  const onlyNumbers = zoomOptions.filter(Number);

  const nextZoomIndex = onlyNumbers.indexOf(currentZoom) + consecutation;
  if (nextZoomIndex === -1 || nextZoomIndex === onlyNumbers.length) {
    return zoomIndex;
  }

  const nextZoom = onlyNumbers[nextZoomIndex];
  return zoomOptions.indexOf(nextZoom);
};

export const createViewerSlice = (set: Set): ViewerSlice => ({
  destination: null,
  activeAreaId: null,
  setDestination: (destination, areaId) => set({ destination, ...(areaId ? { activeAreaId: areaId } : {}) }),
  resetActiveArea: () => set({ activeAreaId: null }),
  pageNum: { value: 1, exact: true },
  setPageNum: pageNum => set({ pageNum }),
  incPageNum: () => set(state => ({ pageNum: { value: state.pageNum.value + 1, exact: true } })),
  decPageNum: () => set(state => ({ pageNum: { value: state.pageNum.value - 1, exact: true } })),
  pageCount: 0,
  setPageCount: pageCount => set({ pageCount }),
  pageDimensions: {},
  setPageDimensions: pageDimensions => set({ pageDimensions }),
  zoomIndex: zoomOptions.indexOf(SpecialZoomLevel.PageWidth),
  setZoomIndex: zoomIndex => set({ zoomIndex }),
  incZoomIndex: () => set(state => ({ zoomIndex: consecuteIndex(state.zoomIndex, 1) })),
  decZoomIndex: () => set(state => ({ zoomIndex: consecuteIndex(state.zoomIndex, -1) })),
});
